import { useEventStore } from "@/event";
import {
	parseEventGeneration,
	sanitizeOverview,
} from "@/event/sync/overviewPatchGuard";
import GameError, { GameErrorType } from "@/libs/GameError";
import useUserStore from "@/store/useUserStore";
import type {
	EventMapCollectablesResult,
	EventMilestonesState,
	EventOverviewResult,
} from "@/types";
import type { Socket } from "socket.io-client";

export type EventSocketTransportDeps = {
	handleTimeoutError: (message: string, error: unknown) => void;
	requestTimeout: number;
};

type StatusResponse = { status: boolean };

type EventOverviewResponse = StatusResponse & {
	eventOverview: Partial<EventOverviewResult>;
};

/** Tracks server event generation ack (see useUserStore.appliedEventGeneration). */
export function syncAppliedEventGeneration(generation?: number | null): void {
	const parsed = parseEventGeneration(generation);
	if (parsed === undefined) return;
	useUserStore().setAppliedEventGeneration(parsed);
}

export function applyEventOverviewOnLoad(
	overview: Partial<EventOverviewResult>,
): void {
	useEventStore().$setState(overview);
	syncAppliedEventGeneration(overview.eventGeneration);
}

export function appliedEventGenerationForSave(): number {
	return useUserStore().appliedEventGeneration;
}

export function eventMapCollectablesForSave(
	include: boolean,
): EventMapCollectablesResult | undefined {
	if (!include) return undefined;
	return useEventStore().getEventMapCollectablesPayload();
}

export const fetchEventOverview = (
	socket: Socket,
	deps: EventSocketTransportDeps,
): Promise<Partial<EventOverviewResult>> =>
	new Promise<Partial<EventOverviewResult>>((resolve, reject) => {
		const emitName = "get_event_overview";

		socket
			.timeout(deps.requestTimeout)
			.emit(
				emitName,
				(error: unknown, data: EventOverviewResponse): void => {
					try {
						deps.handleTimeoutError(
							"Socket error occurred while getting event overview",
							error,
						);

						if (!data) {
							throw new GameError(
								GameErrorType.BE_ERROR,
								"No data received",
							);
						}

						if (!data.status) {
							throw new GameError(
								GameErrorType.BE_ERROR,
								"Failed to get event overview",
							);
						}

						const overview = sanitizeOverview(data.eventOverview);
						applyEventOverviewOnLoad(overview);
						resolve(overview);
					} catch (e) {
						reject(e);
					}
				},
			);
	});

export const emitEventMilestonesSave = (
	socket: Socket,
	deps: EventSocketTransportDeps,
	payload: { state: EventMilestonesState },
): Promise<void> =>
	new Promise<void>((resolve, reject) => {
		const emitName = "update_event_milestones";

		socket
			.timeout(deps.requestTimeout)
			.emit(
				emitName,
				payload,
				(error: unknown, data: StatusResponse): void => {
					try {
						deps.handleTimeoutError(
							"Socket error occurred while sending event milestones save event",
							error,
						);

						if (!data) {
							throw new GameError(
								GameErrorType.BE_ERROR,
								"No data received",
							);
						}

						if (!data.status) {
							throw new GameError(
								GameErrorType.BE_ERROR,
								"Failed to save event milestones",
							);
						}

						resolve();
					} catch (e) {
						reject(e);
					}
				},
			);
	});

export const emitEventPassPurchased = (
	socket: Socket,
	deps: EventSocketTransportDeps,
	payload: { status: boolean },
): Promise<void> =>
	new Promise<void>((resolve, reject) => {
		const emitName = "update_event_pass_purchased";

		socket
			.timeout(deps.requestTimeout)
			.emit(
				emitName,
				payload,
				(error: unknown, data: StatusResponse): void => {
					try {
						deps.handleTimeoutError(
							"Socket error occurred while sending event pass purchased save event",
							error,
						);

						if (!data) {
							throw new GameError(
								GameErrorType.BE_ERROR,
								"No data received",
							);
						}

						if (!data.status) {
							throw new GameError(
								GameErrorType.BE_ERROR,
								"Failed to save event pass purchased",
							);
						}

						resolve();
					} catch (e) {
						reject(e);
					}
				},
			);
	});
